import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  KeyValueDiffer,
  KeyValueDiffers,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { questionTypes } from '../question-types';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import {
  ProcessAltQuestionModel,
  ProcessQuestionModel,
  ProcessQuestionTypeModel,
} from '../../models/questions.model';
import { Store } from '@ngrx/store';
import { AppState } from '../../../app.service';
import { UpdateQuestion } from '../../actions/questions.actions';

@Component({
  selector: 'mj-single-question',
  templateUrl: './single-question.component.html',
  styleUrls: ['./single-question.component.scss'],
})
export class SingleQuestionComponent implements OnInit {
  private newQuestionDiffer: KeyValueDiffer<string, any>;
  newQuestion: ProcessQuestionModel;

  @ViewChild('questionContainer', { static: true }) questionContainer;
  @ViewChild('preview', { static: true }) preview;
  @ViewChild('spec', { static: true }) spec;

  @Input() isSuggestion: boolean;
  @Input() hide: boolean;
  @Input() stepType: number;
  @Input() isEditable: boolean = true;
  @Input() question: ProcessQuestionModel;

  @Output() deleteClicked = new EventEmitter();

  extended: boolean;
  isAnswerValid: boolean = false;

  questionTypes = questionTypes;
  selectedType = questionTypes[0];
  ProcessQuestionTypeModel = ProcessQuestionTypeModel;

  constructor(
    private ref: ChangeDetectorRef,
    private snackBar: MatSnackBar,
    public translate: TranslateService,
    private store: Store<AppState>,
    private differs: KeyValueDiffers
  ) {}

  ngOnInit() {
    if (this.stepType === 0) {
      this.questionTypes = this.questionTypes.filter(
        q => q.type !== ProcessQuestionTypeModel.OneWayVideo
      );
    }

    if (!this.question.question_text && this.isEditable && !this.isSuggestion) {
      this.extended = true;
      this.assignContainerHeight();
    }
    this.newQuestion = { ...this.question };
    this.newQuestionDiffer = this.differs.find(this.newQuestion).create();
    this.selectedType = questionTypes.find(
      q => q.type === this.newQuestion.type
    );
    this.extended = this.isEditable && !this._isValid();
    if (
      this.selectedType.type === 3 &&
      !this.isSuggestion &&
      !this.newQuestion.alternatives.length
    ) {
      this.onTypeChanged();
    }
    this.assignContainerHeight();
  }

  onAddNewAlternative() {
    if (this.newQuestion.alternatives.length === 9) {
      return this.translate
        .get('process_cannot_add_answer_9')
        .pipe(take(1))
        .subscribe(msg => {
          return this.snackBar.open(msg, null, {
            duration: 3000,
          });
        });
    }
    const newAlt: ProcessAltQuestionModel = {
      id: this.newQuestion.alternatives.length + 1,
      answer_text: '',
      is_correct: false,
    };

    this.newQuestion.alternatives = [...this.newQuestion.alternatives];
    this.newQuestion.alternatives.push(newAlt);
    this.assignContainerHeight();
    this.dispatchUpdatedEvent();
  }

  assignContainerHeight(toggleExtend?) {
    // just to make it async
    setTimeout(() => {
      if (toggleExtend && !this.isSuggestion && this.isEditable) {
        this.extended = !this.extended;
      }
      const previewHeight = this.preview.nativeElement.offsetHeight;
      const specHeight = this.spec.nativeElement.offsetHeight;
      this.questionContainer.nativeElement.style.minHeight = `${
        this.extended ? specHeight : previewHeight
      }px`;
    });
  }

  assignQuestionValues() {
    this.newQuestion.alternatives = this.isAlternativeType()
      ? this.newQuestion.alternatives
      : [];
    this.newQuestion.debug = this.selectedType.name;
    this.newQuestion.type = this.selectedType.type;
    this.dispatchUpdatedEvent();
  }

  onAlternativeClicked(item) {
    this.newQuestion.alternatives = [...this.newQuestion.alternatives].map(
      alt => {
        if (alt.answer_text === item.answer_text) {
          alt.is_correct = !alt.is_correct;
        }
        return alt;
      }
    );
    this.dispatchUpdatedEvent();
  }

  dispatchUpdatedEvent() {
    this.store.dispatch(
      new UpdateQuestion({
        questionToUpdate: this.newQuestion,
      })
    );
  }

  onYesNoClicked(item, tooltip?) {
    this.newQuestion.alternatives = [...this.newQuestion.alternatives].map(
      alt => {
        alt = Object.assign({}, alt, { is_correct: item === alt });
        return alt;
      }
    );
    console.log(this.newQuestion.alternatives);
    if (tooltip) {
      tooltip.style.display = 'none';
    }
    this.dispatchUpdatedEvent();
  }

  onTypeChanged() {
    let yes = 'Yes';
    let no = 'No';
    this.translate
      .get('generic_yes')
      .pipe(take(1))
      .subscribe(y => (yes = y));
    this.translate
      .get('generic_no')
      .pipe(take(1))
      .subscribe(n => (no = n));
    if (this.selectedType.type === ProcessQuestionTypeModel.YesNo) {
      this.newQuestion.alternatives = [
        {
          id: 1,
          answer_text: yes,
          is_correct: false,
        },
        {
          id: 2,
          answer_text: no,
          is_correct: false,
        },
      ];
    } else {
      this.newQuestion.alternatives = [];
    }
    this.assignQuestionValues();
    this.assignContainerHeight();
    this.dispatchUpdatedEvent();
  }

  onDeleteClicked() {
    this.deleteClicked.emit(null);
  }

  setAltMessage(alt: ProcessAltQuestionModel, input) {
    this.newQuestion.alternatives = this.newQuestion.alternatives.map(
      question => {
        if (question === alt) {
          return Object.assign({}, question, {
            answer_text: input.value,
          });
        } else {
          return question;
        }
      }
    );
  }

  onRemoveAlt(altQuestion) {
    const index = this.newQuestion.alternatives.indexOf(altQuestion);
    const newActions = [...this.newQuestion.alternatives];
    newActions.splice(index, 1);
    this.newQuestion.alternatives = [...newActions];
    this.dispatchUpdatedEvent();
    this.assignContainerHeight(false);
  }

  private _isValid(): boolean {
    let validationStatus = true;
    let rightAnswers = [];
    if (!this.question.question_text) {
      return false;
    }
    if (this.isAlternativeType()) {
      rightAnswers = this.newQuestion.alternatives.filter(
        answer => answer.is_correct
      );
      validationStatus = !!rightAnswers.length;
    }

    return validationStatus;
  }

  isAlternativeType() {
    return (
      (this.selectedType.type &&
        this.selectedType.type === ProcessQuestionTypeModel.YesNo) ||
      this.selectedType.type === ProcessQuestionTypeModel.MultiChoice ||
      this.selectedType.type === ProcessQuestionTypeModel.SingleChoice
    );
  }
}
